<template>
  <div class="ns-searchbar-input" :class="{ 'new-header': newHeader, focused }">
    <form class="searchbar-form" action="" @submit.prevent>
      <NsIcon class="search-icon" icon-name="icon-search-mc" size="custom" />
      <input
        id="searchInput"
        ref="input"
        autocomplete="off"
        spellcheck="false"
        type="text"
        class="search-input"
        :placeholder="placeholderText"
        :value="value"
        @input="$emit('input', $event)"
        @focus="$emit('focus')"
        @keyup="$emit('analyze-key', $event)"
      />
      <div
        v-show="value.length && !noSearchButton"
        class="clear-icon-container"
        @click="clearInput"
      >
        <NsIcon
          class="clear-icon"
          icon-name="icon-circle-close-light"
          size="custom"
        />
      </div>

      <NsSearchbarTooltip
        :message="translations.iconsContainerTooltip"
        :disabled="!showTooltip"
        @close="$emit('disable-input-tooltip')"
      />
    </form>

    <div class="cancel-button button" tabindex="-1" @click="$emit('blur')">
      <span>{{ translations.cancel }}</span>
    </div>
  </div>
</template>

<script>
import { NsIcon } from "@nuskin/design-components";
import NsSearchbarTooltip from "./NsSearchbarTooltip.vue";

export default {
  name: "NsSearchbarInput",
  components: { NsSearchbarTooltip, NsIcon },
  props: {
    newHeader: Boolean,
    focused: Boolean,
    value: {
      type: String,
      default: ""
    },
    translations: {
      type: Object,
      default: function() {
        return {};
      }
    },
    noSearchButton: Boolean,
    showTooltip: Boolean
  },
  computed: {
    placeholderText() {
      if (window.outerWidth > 1023) {
        return this.translations.desktopPlaceholder || "";
      } else if (this.translations.mobilePlaceholder) {
        return this.translations.mobilePlaceholder || "";
      } else {
        return this.translations.desktopPlaceholder || "";
      }
    }
  },
  watch: {
    focused(isFocused) {
      if (isFocused) {
        this.$refs.input.focus();
      } else {
        this.$refs.input.blur();
      }
    }
  },
  methods: {
    clearInput() {
      this.$emit("clear-input");
      this.$refs.input.focus();
    }
  }
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/other";
@use '~@nuskin/ns-core-styles/src/scss/typographyVariables';

.ns-searchbar-input {
  display: flex;
  height: 100%;
  background-color: transparent;
  // @include ns-searchbar-input-button();
}

.searchbar-form {
  position: relative;
  display: flex;
  align-items: center;
  margin: 0;
  width: 100%;
}

.slotted {
  .searchbar-form {
    margin: 0;
    height: 34px;
  }
}

input.search-input {
  position: relative;
  border: none !important;
  padding: 0;
  width: 100%;
  height: 100%;
  max-height: none;
  min-height: unset;
  font-size: 14px !important;
  color: map.get(colors.$themeColors, color-interactive-8);
  background-color: transparent;
  font-family: map.get(typographyVariables.$typography, font-family-primary);
  font-weight: map.get(typographyVariables.$typography, font-weight-regular);
  @include global.p-medium();
  box-shadow: none;
  outline: none;

  &:focus {
    border: none;
    outline: none;
    box-shadow: none;
  }
}

.new-header input.search-input {
  font-size: 14px;
  font-weight: normal;
}

.search-icon,
.clear-icon {
  flex-shrink: 0;
  margin: 0 8px;
  height: 18px;
  width: 18px;
  color: map.get(colors.$themeColors, color-interactive-8);
  -webkit-tap-highlight-color: transparent;
}

.focused {
  .search-icon,
  .clear-icon {
    color: map.get(colors.$themeColors, color-interactive-7);
  }
}

.clear-icon-container {
  cursor: pointer;
}

.button {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 7px 17px;
  border-left: none !important;
  border-right: map.get(other.$otherUtils, global-border);
  font-size: 13px;
  font-weight: map.get(typographyVariables.$typography, font-weight-semibold);
  letter-spacing: 0.8px;
  line-height: 16px;
  text-transform: uppercase;
  transition: map.get(other.$otherUtils, global-transition);
  outline: none;
  cursor: pointer;

  &.focused,
  &:hover {
    color: map.get(colors.$themeColors, color-interactive-7);
  }

  span {
    white-space: nowrap;
  }
}

.cancel-button {
  display: none;
}

@media (max-width: 1023px) {
  .searchbar-form {
    padding-left: 10px;
  }
  .rtl.not_focused .search-input {
    margin-right: 27px;
  }

  .rtl.focused .search-input {
    margin-right: 10px;
    padding-right: 30px;
  }

  .focused.not_slotted .search-input {
    border: map.get(other.$otherUtils, global-border-active);
    padding: 12px 10px 12px 33px;
    font-size: 16px;
  }

  .search-input {
    position: relative;
    padding: 0 6px 0 18px;
    font-size: 14px;
  }

  .focused.not_slotted {
    .search-icon {
      position: absolute;
      left: 10px;
      height: 20px;
      width: 22px;
      z-index: 1;
    }

    .clear-icon {
      height: 18px;
      width: 18px;
      color: map.get(colors.$themeColors, color-interactive-7);
    }

    .clear-icon-container {
      position: absolute;
      right: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 100%;
      cursor: pointer;
    }

    &[dir="rtl"] {
      .clear-icon-container {
        right: 5px;
      }
    }

    .cancel-button {
      display: inline-flex;
      border: none;
    }
  }

  .cancel-button {
    opacity: 0;
    visibility: hidden;
    transition: none;
  }

  .focused .cancel-button {
    opacity: 1;
    visibility: visible;
  }
}

@media (min-width: 1024px) {
  .button {
    position: relative;
    border-left: map.get(other.$otherUtils, global-border);
    border-radius: 0 5px 5px 0;

    &.new-header {
      flex-shrink: 0;
      padding: 2px 10px;
      border-left: 1px solid rgb(236, 237, 237);
      border-radius: 0 3px 3px 0;
      width: 98px;
    }

    &.focused {
      border: map.get(other.$otherUtils, global-border-active);
      border-left: map.get(other.$otherUtils, global-border-active);
    }

    &.submit-button-active {
      border: map.get(other.$otherUtils, global-border-active);
      color: white;
      background-color: map.get(colors.$themeColors, color-interactive-7);
    }
  }

  .rtl {
    .button {
      border-left: map.get(other.$otherUtils, global-border) !important;
      border-right: none !important;
      border-radius: 5px 0 0 5px;

      &.focused {
        border-right: none;
        border-right: map.get(other.$otherUtils, global-border-active);
        border-left: map.get(
          other.$otherUtils,
          global-border-active
        ) !important;
      }
    }

    .search-input {
      margin-right: 27px;
    }
  }
}
</style>
